/**
 * Tiny date helpers — DD/MM/YYYY (Australian English) per OnsiteCore
 * platform §9.
 *
 * We deliberately do not pull in moment/dayjs for the two formats we use;
 * if a third use case appears, revisit.
 */

const pad = (n: number) => n.toString().padStart(2, '0');

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * Money in Australian dollars, no cents — used for CMDB cost showback (estimated
 * recurring figures, so sub-dollar precision is noise). Returns '—' for null.
 */
const audFormatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
export function formatMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return audFormatter.format(value);
}

/**
 * Money in Australian dollars, CENTS AND ALL.
 *
 * ⛔ Separate from {@link formatMoney} deliberately. That one rounds, which is
 * right for an estimated recurring figure and wrong for a price: $1,299.50
 * shown as $1,300 is a number somebody will copy into a purchase order.
 * Anywhere the figure is, or becomes, an amount of money that moves, use this.
 */
const audExactFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2,
});
export function formatMoneyExact(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return audExactFormatter.format(value);
}

/**
 * Formats a naive "wall-clock" datetime as DD/MM/YYYY HH:MM from its own literal
 * fields, with NO timezone conversion. Use for values that are already in the
 * intended local time and must be shown verbatim — e.g. OnsiteLiveConfig's
 * Sydney-local `ExpiryDateTime` / on-prem `Oscar_AuditLog.LogDateTime`, where
 * {@link formatDateTime} (which assumes UTC) would shift them by the Sydney
 * offset. Viewer-timezone-independent.
 */
export function formatWallClock(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const s = typeof value === 'string' ? value : value.toISOString();
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
  if (!m) return formatDate(value);
  const [, y, mo, d, hh, mm] = m;
  return `${d}/${mo}/${y} ${hh}:${mm}`;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  // API DATETIME2 values come back UTC without a timezone designator, so the
  // browser would parse them as local. Append 'Z' so they're read as UTC; the
  // local getHours()/getDate() below then render in the viewer's local time.
  const d = typeof value === 'string'
    ? new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`)
    : value;
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
